import { toRaw, watch, type MaybeRef } from "vue";
import { useForm, type GenericObject, type TypedSchema } from "vee-validate";
import cloneDeep from "lodash/cloneDeep";

export interface ModelFormOptions<TValues extends GenericObject, TOutput extends GenericObject = TValues> {
  /**
   * Returns the data to validate, shaped like the schema, read from the existing state (refs, store objects,
   * props). Called reactively: every change re-syncs the form values.
   * @example () => ({ name: config.value.name, url: url.value, headers: headers.value })
   */
  model: () => GenericObject;
  /** `toTypedSchema(...)` result, or a ref/computed of one when the rules depend on other state */
  validationSchema: MaybeRef<TypedSchema<TValues, TOutput>>;
}

/**
 * `useForm()` for components whose values already live somewhere else (a store object, a prop model, refs)
 * and are edited in place through `v-model`s — the common case in the hub.
 *
 * Plain `useForm()` only knows the values of *mounted* fields, which bites in two ways:
 * - fields that aren't rendered (collapsed accordion, other tab/step) are `undefined` in the form, so a schema
 *   that requires them keeps `meta.valid` false with no visible error;
 * - unmounting a field deletes its value from the form (and splices arrays), which a v-model-synced field
 *   then writes back into your state.
 *
 * Here the form mirrors `model()` at all times instead (and never drops values on unmount), so the schema always
 * validates the real data. Bind fields with `v-model` to your state as usual (`<TextfieldPlus v-model name>` or
 * `<VeeField v-model name>`); they're only needed where an error should be shown. Make rules for fields that can
 * be hidden conditional in the schema (computed schema / `superRefine`), as their data is still validated.
 *
 * @returns the regular `useForm()` context (`meta`, `validate`, `handleSubmit`, `resetForm`, …)
 */
export function useModelForm<TValues extends GenericObject, TOutput extends GenericObject = TValues>(
  options: ModelFormOptions<TValues, TOutput>,
) {
  const snapshot = () => cloneDeep(toRaw(options.model())) as TValues;

  const form = useForm<TValues, TOutput>({
    validationSchema: options.validationSchema,
    // A full snapshot is a valid (deep-partial) initial value; the cast only satisfies VeeValidate's PartialDeep type
    initialValues: snapshot() as never,
    keepValuesOnUnmount: true,
  });

  watch(
    options.model,
    () => {
      form.setValues(snapshot() as Parameters<typeof form.setValues>[0], false);
      // Refresh `meta.valid` and the errors that are already shown; untouched fields stay quiet
      void form.validate({ mode: "validated-only" });
    },
    { deep: true },
  );

  return form;
}
